import React, { useState } from 'react';
import {
  Dumbbell,
  Plus,
  Sparkles,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import type { TrainingGoal, TrainingLevel, ProgressionModel } from '../types';
import {
  TRAINING_GOAL_LABELS,
  TRAINING_LEVEL_LABELS,
  PROGRESSION_MODEL_LABELS,
} from '../types';
import { usePlanStore } from '../store/usePlanStore';
import { PLAN_TEMPLATES } from '../data/templates';

export default function PlanConfig() {
  const { plan, createPlan, loadPlan, updatePlanConfig, clearPlan } = usePlanStore();
  const [collapsed, setCollapsed] = useState(!!plan);
  const [showTemplates, setShowTemplates] = useState(false);

  // ── Create new plan form state ───────
  const [form, setForm] = useState({
    name: 'Nowy program',
    clientName: '',
    goal: 'hypertrophy' as TrainingGoal,
    trainingLevel: 'intermediate' as TrainingLevel,
    mesocycleLength: 4,
    frequency: 4,
    fatigueTolerance: 6,
    progressionModel: 'undulating' as ProgressionModel,
  });

  const handleCreate = () => {
    createPlan(form);
  };

  const handleLoadTemplate = (templateId: string) => {
    const t = PLAN_TEMPLATES.find((t) => t.id === templateId);
    if (t) {
      loadPlan(t.factory());
      setShowTemplates(false);
    }
  };

  // ── No plan → show creation form ─────
  if (!plan) {
    return (
      <div className="max-w-2xl mx-auto animate-fade-in">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-100 dark:bg-brand-950 mb-4">
            <Dumbbell size={32} className="text-brand-600" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight mb-2">Kreator Programów Treningowych APEX</h1>
          <p className="text-slate-500 dark:text-slate-400">
            Projektuj inteligentne, oparte na nauce
          </p>
        </div>

        {/* Template quick-start */}
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
            Szybki start - Szablony
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {PLAN_TEMPLATES.map((t) => (
              <button
                key={t.id}
                onClick={() => handleLoadTemplate(t.id)}
                className="text-left p-4 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-brand-400 dark:hover:border-brand-500 hover:shadow-md transition-all group bg-white dark:bg-slate-900"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles size={14} className="text-brand-500" />
                  <span className="font-semibold text-sm">{t.name}</span>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2">
                  {t.description}
                </p>
                <div className="flex gap-2 mt-2">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-medium">
                    {TRAINING_GOAL_LABELS[t.goal]}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-medium">
                    {t.frequency}×/tydz.
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Custom creation form */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-6">
          <h2 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">
            Lub zbuduj od zera
          </h2>

          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                Nazwa programu
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Goal */}
              <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Cel</label>
                <select
                  value={form.goal}
                  onChange={(e) => setForm({ ...form, goal: e.target.value as TrainingGoal })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                >
                  {Object.entries(TRAINING_GOAL_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>

              {/* Level */}
              <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                  Poziom zaawansowania
                </label>
                <select
                  value={form.trainingLevel}
                  onChange={(e) => setForm({ ...form, trainingLevel: e.target.value as TrainingLevel })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                >
                  {Object.entries(TRAINING_LEVEL_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>

              {/* Mesocycle length */}
              <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                  Mezocykl (tygodnie)
                </label>
                <input
                  type="number"
                  min={2}
                  max={12}
                  value={form.mesocycleLength}
                  onChange={(e) => setForm({ ...form, mesocycleLength: parseInt(e.target.value) || 4 })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                />
              </div>

              {/* Frequency */}
              <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                  Sesji / tydzień
                </label>
                <input
                  type="number"
                  min={1}
                  max={7}
                  value={form.frequency}
                  onChange={(e) => setForm({ ...form, frequency: parseInt(e.target.value) || 4 })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                />
              </div>

              {/* Fatigue tolerance */}
              <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                  Tolerancja zmęczenia ({form.fatigueTolerance}/10)
                </label>
                <input
                  type="range"
                  min={1}
                  max={10}
                  value={form.fatigueTolerance}
                  onChange={(e) => setForm({ ...form, fatigueTolerance: parseInt(e.target.value) })}
                  className="w-full accent-brand-500"
                />
              </div>

              {/* Progression */}
              <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                  Model progresji
                </label>
                <select
                  value={form.progressionModel}
                  onChange={(e) => setForm({ ...form, progressionModel: e.target.value as ProgressionModel })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                >
                  {Object.entries(PROGRESSION_MODEL_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
            </div>

            <button
              onClick={handleCreate}
              className="w-full mt-2 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2"
            >
              <Plus size={18} />
              Stwórz program
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Existing plan → collapsible config ──
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl mb-6 animate-fade-in">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center justify-between px-5 py-3 text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl transition"
      >
        <span>Ustawienia programu - {plan.name}</span>
        {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
      </button>

      {!collapsed && (
        <div className="px-5 pb-5 border-t border-slate-100 dark:border-slate-800 pt-4 animate-slide-down">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 text-sm">
            {/* Inline editors for plan config */}
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Nazwa</label>
              <input
                type="text"
                value={plan.name}
                onChange={(e) => updatePlanConfig({ name: e.target.value })}
                className="w-full px-2 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Klient</label>
              <input
                type="text"
                value={plan.clientName || ''}
                onChange={(e) => updatePlanConfig({ clientName: e.target.value })}
                placeholder="opcjonalnie"
                className="w-full px-2 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Cel</label>
              <select
                value={plan.goal}
                onChange={(e) => updatePlanConfig({ goal: e.target.value as TrainingGoal })}
                className="w-full px-2 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
              >
                {Object.entries(TRAINING_GOAL_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Poziom</label>
              <select
                value={plan.trainingLevel}
                onChange={(e) => updatePlanConfig({ trainingLevel: e.target.value as TrainingLevel })}
                className="w-full px-2 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
              >
                {Object.entries(TRAINING_LEVEL_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Tygodnie</label>
              <input
                type="number"
                min={2}
                max={12}
                value={plan.mesocycleLength}
                onChange={(e) => updatePlanConfig({ mesocycleLength: parseInt(e.target.value) || 4 })}
                className="w-full px-2 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">
                Tolerancja zmęczenia ({plan.fatigueTolerance}/10)
              </label>
              <input
                type="range"
                min={1}
                max={10}
                value={plan.fatigueTolerance}
                onChange={(e) => updatePlanConfig({ fatigueTolerance: parseInt(e.target.value) })}
                className="w-full accent-brand-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Progresja</label>
              <select
                value={plan.progressionModel}
                onChange={(e) => updatePlanConfig({ progressionModel: e.target.value as ProgressionModel })}
                className="w-full px-2 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
              >
                {Object.entries(PROGRESSION_MODEL_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => { usePlanStore.getState().goToDashboard(); }}
              className="px-3 py-1.5 rounded-lg text-xs font-medium text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
            >
              Zamknij i wróć do pulpitu
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
